// Pure (non-JSX) extraction layer for the DSL reference: turn a
// registered op schema + meta into a flat, render-ready row. Kept
// separate from `components.tsx` so the JSX module exports only
// components (lint: `useComponentExportOnlyModules`) and so the
// extraction is unit-testable without React.

import type { z } from 'zod';

import { type BaseOpMeta, type Effect, type OpExample, opNameOf, type TerseGroupId } from '../op-meta.ts';
import type { EnumDomainResolver } from './types.ts';
import { collectKwargs, describeOf, type KwargDoc, paramsSignature } from './zod-walk.ts';

/**
 * Flat row extracted from an op schema + its registered meta, enriched
 * with the Zod-walked positional/keyword signature. The same shape
 * covers top-level ops, pipeline verbs, agg-context funcs, dashboard
 * tiles/sub-forms — they differ only in which registry holds the meta.
 */
export type OpDocEntry = {
  readonly name: string;
  readonly description: string;
  readonly effect: Effect;
  readonly example: OpExample | undefined;
  readonly typeNote: string | undefined;
  readonly params: string;
  readonly kwargs: readonly KwargDoc[];
  /** Doc-compaction group, or undefined for a full standalone entry. */
  readonly terseGroup: TerseGroupId | undefined;
  /** Hand-authored signature override (see `BaseOpMeta.docSignature`). */
  readonly signature: string | undefined;
};

/** A scope rendered as a section: heading, optional blurb, op list. */
export type OpDocScopeView = {
  readonly name: string;
  readonly description: string | undefined;
  readonly entries: readonly OpDocEntry[];
};

/**
 * Build a doc entry from a schema + its registered meta. Generic over
 * meta type — every scope's meta intersects with `BaseOpMeta`. The
 * optional `resolve` maps enum member sets to a domain name so kwarg
 * types render as `Color` (expanded once in the glossary) rather than
 * the inline value list.
 */
export const entryOf = (
  schema: z.ZodObject,
  meta: BaseOpMeta | undefined,
  resolve?: EnumDomainResolver,
): OpDocEntry => ({
  name: opNameOf(schema),
  // Author-supplied `summary` wins over the Zod `.describe()` text.
  description: meta?.summary ?? describeOf(schema) ?? '',
  effect: meta?.effect ?? 'pure',
  example: meta?.example,
  typeNote: meta?.typeNote,
  params: paramsSignature(schema, resolve),
  kwargs: collectKwargs(schema, resolve),
  terseGroup: meta?.terseGroup,
  signature: meta?.docSignature,
});

/**
 * The one-line call signature: `(name <pos>… :kw <type> …)`. Optional
 * kwargs are wrapped in `[...]` so required vs optional reads at a
 * glance — this is the single place optionality and type are stated
 * (per-kwarg rows carry only the prose semantics).
 */
export function signatureOf(e: OpDocEntry): string {
  if (e.signature !== undefined) {
    return e.signature;
  }
  const parts = [
    e.name,
    e.params,
    ...e.kwargs.map((k) => (k.optional ? `[:${k.name} ${k.type}]` : `:${k.name} ${k.type}`)),
  ].filter((p) => p !== '');
  return `(${parts.join(' ')})`;
}

/** Heading, shared blurb and one worked example for a terse group. */
export type TerseGroupDef = {
  readonly heading: string;
  readonly blurb: string;
  readonly example: string;
};

/**
 * SSOT for the collapsed-group lines. The shared semantics that used
 * to be copy-pasted into every member op's `.describe()` (chain
 * behaviour, the equality rule, lisp truthiness, identity elements)
 * live here once. Op membership is NOT listed here — it is carried by
 * each op's `terseGroup` meta tag, so a newly added op auto-joins its
 * group's line with no edit to this table.
 */
export const TERSE_GROUPS: Record<TerseGroupId, TerseGroupDef> = {
  arith: {
    heading: 'Arithmetic',
    blurb:
      'Standard math, variadic where sensible: (+ …)→sum (0 for none), (- a)→negate / (- a b c)→a-b-c, (* …)→product (1 for none), (/ a b c)→left-fold division; mod/pow and the unary fns (abs neg round floor ceil sqrt log exp) take fixed arity. round halves toward +∞.',
    example: '(round (/ (+ a b) 2))',
  },
  compare: {
    heading: 'Comparison',
    blurb:
      'Chainable: (< a b c) ⇔ a<b<c (same for <= > >=). = != in share one equality — strict for number/boolean, case-insensitive for strings, date-aware when either side is a date/timestamp. prefix is a case-insensitive string-prefix test; (re-match s pattern) is a JS-regex test; (fuzzy s pattern :threshold 0.4) is a fuzzy string match (0 = exact … 1 = anything).',
    example: '(and (>= age 18) (in role ["admin" "editor"]))',
  },
  logic: {
    heading: 'Logical & null',
    blurb:
      'and/or are variadic and short-circuit (lisp truthiness — only false/null/undefined are falsy; (and)→true, (or)→false); not negates; is-null / is-not-null test null-or-missing.',
    example: '(and (is-not-null x) (or flagA flagB))',
  },
  string: {
    heading: 'String',
    blurb:
      'Self-evident from name + JS semantics: concat (null→""), upper, lower, trim, length, contains, starts-with, ends-with (predicates → false on null subject), index-of (→ -1 on null subject). The non-obvious ones — substring, replace, split — are listed below.',
    example: '(upper (trim (concat first " " last)))',
  },
  fill: {
    heading: 'Fill',
    blurb:
      'Replace nulls within the frame: forward-fill (LOCF) takes the nearest preceding non-null, backward-fill (NOCB) the nearest following. Variadic — names the target columns, or fills all non-key columns when none given; :limit n caps the fill run.',
    example: '(-> readings (forward-fill :level :order-by :ts :partition-by [:tank]))',
  },
  shift: {
    heading: 'Shift',
    blurb:
      'Add a column with the value :offset rows earlier (lag) / later (lead) within the frame — SQL LAG/LEAD. :offset defaults to 1; output defaults to <col>_lag<offset> / <col>_lead<offset>. Positional shift — does not skip nulls.',
    example: '(-> trades (lag :price :order-by :ts :partition-by [:symbol] :offset 1))',
  },
  pick: {
    heading: 'First/last value',
    blurb:
      'Add a column with the first-value / last-value non-null value of the column within each frame, broadcast to every row (null-skipping; output defaults to <col>_first / <col>_last).',
    example: '(-> readings (last-value :level :order-by :ts :partition-by [:tank]))',
  },
  rolling: {
    heading: 'Rolling',
    blurb:
      'Trailing-window aggregates of a column: rolling-mean (smoothing), rolling-sum, rolling-min, rolling-max, rolling-stddev (volatility). :window is a time span ("7d", "12h") measured along the frame order; output defaults to <col>_rolling_<fn>.',
    example: '(-> well-rates (rolling-mean :rate :window "7d"))',
  },
  cum: {
    heading: 'Cumulative',
    blurb:
      'Running values within the frame: cumsum (use for per-period volumetric quantities — for a flow rate integrate with cum-integral instead), cummax (peak-to-date), cummin (trough-to-date).',
    example: '(-> daily (cumsum :produced-volume))',
  },
  diff: {
    heading: 'Change',
    blurb:
      'Per-row change vs the previous row in the frame: delta = v − vprev (null on first row and across gaps); pct-change = (v − vprev)/vprev (null when vprev is 0 or missing); rate-of-change = Δv/Δt normalised to :per (default day) — the discrete derivative.',
    example: '(-> daily (delta :reading))',
  },
  'agg-basic': {
    heading: 'Standard reducers',
    blurb: 'Reduce the named column over the group (null-skipping); range = max − min.',
    example: '(sum :revenue)',
  },
};

/** A populated group: its id, doc, and the member op names (in walk order). */
export type TerseGroupView = {
  readonly id: TerseGroupId;
  readonly def: TerseGroupDef;
  readonly names: readonly string[];
};

/**
 * Split a scope's entries into collapsed groups (rendered as one line
 * each) and the remaining standalone entries. Group order is the
 * `TERSE_GROUPS` declaration order (its keys are the SSOT — no
 * separate order list to keep in sync); member order follows the entry
 * walk order. Untagged entries keep their original order.
 */
export function partitionTerse(entries: readonly OpDocEntry[]): {
  readonly groups: readonly TerseGroupView[];
  readonly rest: readonly OpDocEntry[];
} {
  const order = Object.keys(TERSE_GROUPS) as readonly TerseGroupId[];
  const groups = order.flatMap((id): TerseGroupView[] => {
    const names = entries.filter((e) => e.terseGroup === id).map((e) => e.name);
    return names.length === 0 ? [] : [{ id, def: TERSE_GROUPS[id], names }];
  });
  return { groups, rest: entries.filter((e) => e.terseGroup === undefined) };
}
